import { APARTMENT_ROOM_OPTIONS, PROPERTY_TYPES } from "../../../../types";
import type { MarketSubjectSnapshot } from "../../domain/types";
import { CityAutocomplete } from "../../location/CityAutocomplete";
import { StreetAutocomplete } from "../../location/StreetAutocomplete";
import { locationAfterCityChange } from "../../location/streetSearch";
import { effectiveApartmentArea } from "../../domain/propertyMatching";
import { MarketFieldSection, MarketInput, MarketSelect } from "../MarketFields";

const CONDITION_OPTIONS = ["Pôvodný stav", "Čiastočná rekonštrukcia", "Kompletná rekonštrukcia", "Novostavba", "Iné"];
const RECONSTRUCTION_OPTIONS = ["Bez rekonštrukcie", "Čiastočná", "Kompletná", "Novostavba", "Nezistené"];
const ELECTRICITY_OPTIONS = ["Pôvodná", "Čiastočne menená", "Nová", "Nezistené"];
const CORE_OPTIONS = ["Umakartové", "Murované", "Iné", "Nezistené"];
const KITCHEN_OPTIONS = ["Pôvodná", "Staršia", "Modernizovaná", "Nová", "Nezistené"];
const BALCONY_OPTIONS = ["Bez balkóna", "Balkón", "Lodžia", "Terasa", "Viac možností"];
const CELLAR_OPTIONS = ["Áno", "Nie", "Nezistené"];
const ELEVATOR_OPTIONS = ["Áno", "Nie", "Nezistené"];
const ORIENTATION_OPTIONS = ["Sever", "Juh", "Východ", "Západ", "Kombinovaná", "Nezistené"];
const BUILDING_OPTIONS = ["Pôvodný stav", "Čiastočne obnovený", "Kompletne obnovený", "Novostavba", "Nezistené"];

type SubjectStepProps = {
  subject: MarketSubjectSnapshot;
  readOnly: boolean;
  errors: string[];
  onChange: (subject: MarketSubjectSnapshot) => void;
};

export function SubjectStep({ subject, readOnly, errors, onChange }: SubjectStepProps) {
  const update = <Key extends keyof MarketSubjectSnapshot>(key: Key, value: MarketSubjectSnapshot[Key]) => {
    if (!readOnly) onChange({ ...subject, [key]: value });
  };
  const comparedArea = effectiveApartmentArea(subject);
  const areaBasisLabel = comparedArea.basis === "apartment" || comparedArea.basis === "interior"
    ? "čistá plocha bytu"
    : comparedArea.basis === "total_only"
      ? "iba celková plocha · nižšia dôvera"
      : "historická výmera bez určeného základu · nižšia dôvera";
  const updateArea = (key: "apartmentArea" | "cellarArea" | "totalArea", rawValue: string) => {
    if (readOnly) return;
    const value = Number(rawValue) || undefined;
    const previousAreas = subject.areas ?? { areaBasis: "legacy_unspecified" as const };
    const nextAreas = { ...previousAreas, [key]: value };
    if (key === "apartmentArea") {
      nextAreas.areaBasis = value
        ? "apartment"
        : nextAreas.interiorArea
          ? "interior"
          : nextAreas.totalArea
            ? previousAreas.areaBasis === "legacy_unspecified" ? "legacy_unspecified" : "total_only"
            : "legacy_unspecified";
    } else if (key === "totalArea" && !nextAreas.apartmentArea && !nextAreas.interiorArea) {
      nextAreas.areaBasis = value ? "total_only" : "legacy_unspecified";
    }
    const fieldProvenance = { ...(subject.fieldProvenance ?? {}) };
    if (value) {
      fieldProvenance[key] = {
        source: "FISPRO Market · manuálne zadaný subject",
        confidence: key === "apartmentArea" ? "high" : "medium",
        capturedAt: new Date().toISOString(),
        rawValue: value
      };
    } else {
      delete fieldProvenance[key];
    }
    const nextComparedArea = nextAreas.apartmentArea ?? nextAreas.interiorArea ?? nextAreas.totalArea;
    onChange({ ...subject, area: nextComparedArea, areas: nextAreas, fieldProvenance });
  };

  return (
    <div className="market-step-content">
      <div className="market-step-intro">
        <p className="market-kicker">Predmet analýzy</p>
        <h2>Čo presne oceňujeme?</h2>
        <p>Údaje zostanú uložené ako snapshot tejto verzie analýzy.</p>
      </div>

      {errors.length > 0 ? <div className="market-error" role="alert">{errors.join(" ")}</div> : null}

      <div aria-disabled={readOnly} className={readOnly ? "market-readonly" : ""}>
        <MarketFieldSection title="Lokalita a typ">
          <CityAutocomplete
            value={subject.city}
            citySource={subject.citySource}
            disabled={readOnly}
            onChange={(selection) => {
              if (!readOnly) onChange(locationAfterCityChange(subject, selection));
            }}
          />
          <StreetAutocomplete
            value={subject.street}
            municipalityId={subject.municipalityId}
            streetId={subject.streetId}
            streetSource={subject.streetSource}
            disabled={readOnly}
            onChange={(selection) => {
              if (!readOnly) onChange({ ...subject, ...selection });
            }}
          />
          <MarketInput label="Lokalita / mikrolokalita" value={subject.locality} onChange={(value) => update("locality", value)} disabled={readOnly} />
          <MarketSelect label="Typ nehnuteľnosti" value={subject.propertyType} options={PROPERTY_TYPES} onChange={(value) => update("propertyType", value)} disabled={readOnly} />
          <MarketSelect label="Počet izieb" value={subject.rooms} options={APARTMENT_ROOM_OPTIONS} onChange={(value) => update("rooms", value)} disabled={readOnly} />
          <MarketInput label="Plocha bytu / interiéru" type="number" min="1" inputMode="decimal" value={subject.areas?.apartmentArea ?? subject.areas?.interiorArea} suffix="m²" onChange={(value) => updateArea("apartmentArea", value)} disabled={readOnly} />
          <MarketInput label="Plocha pivnice" type="number" min="0" inputMode="decimal" value={subject.areas?.cellarArea} suffix="m²" onChange={(value) => updateArea("cellarArea", value)} disabled={readOnly} />
          <MarketInput label="Celková plocha" type="number" min="1" inputMode="decimal" value={subject.areas?.totalArea} suffix="m²" onChange={(value) => updateArea("totalArea", value)} disabled={readOnly} />
          <div className={`market-area-basis ${comparedArea.confidence === "low" ? "low-confidence" : ""}`}>
            <span>Porovnávaná plocha</span>
            <strong>{comparedArea.value ? `${comparedArea.value.toLocaleString("sk-SK")} m²` : "Nie je určená"}</strong>
            <small>{areaBasisLabel}</small>
          </div>
        </MarketFieldSection>

        <MarketFieldSection title="Technický stav">
          <MarketSelect label="Stav" value={subject.condition} options={CONDITION_OPTIONS} onChange={(value) => update("condition", value)} disabled={readOnly} />
          <MarketSelect label="Rozsah rekonštrukcie" value={subject.reconstruction} options={RECONSTRUCTION_OPTIONS} onChange={(value) => update("reconstruction", value)} disabled={readOnly} />
          <MarketSelect label="Elektroinštalácia" value={subject.electricity} options={ELECTRICITY_OPTIONS} onChange={(value) => update("electricity", value)} disabled={readOnly} />
          <MarketSelect label="Bytové jadro" value={subject.coreType} options={CORE_OPTIONS} onChange={(value) => update("coreType", value)} disabled={readOnly} />
          <MarketSelect label="Kuchyňa" value={subject.kitchen} options={KITCHEN_OPTIONS} onChange={(value) => update("kitchen", value)} disabled={readOnly} />
          <MarketSelect label="Stav bytového domu" value={subject.buildingCondition} options={BUILDING_OPTIONS} onChange={(value) => update("buildingCondition", value)} disabled={readOnly} />
        </MarketFieldSection>

        <MarketFieldSection title="Poloha a vybavenie">
          <MarketSelect label="Balkón / lodžia" value={subject.balconyType} options={BALCONY_OPTIONS} onChange={(value) => update("balconyType", value)} disabled={readOnly} />
          <MarketSelect label="Pivnica" value={subject.cellar} options={CELLAR_OPTIONS} onChange={(value) => update("cellar", value)} disabled={readOnly} />
          <MarketInput label="Poschodie" type="number" inputMode="numeric" value={subject.floor} onChange={(value) => update("floor", value === "" ? undefined : Number(value))} disabled={readOnly} />
          <MarketSelect label="Výťah" value={subject.elevator} options={ELEVATOR_OPTIONS} onChange={(value) => update("elevator", value)} disabled={readOnly} />
          <MarketSelect label="Orientácia" value={subject.orientation} options={ORIENTATION_OPTIONS} onChange={(value) => update("orientation", value)} disabled={readOnly} />
        </MarketFieldSection>

        <MarketFieldSection title="Cena majiteľa">
          <MarketInput
            label="Predstava majiteľa pri analýze"
            type="number"
            min="0"
            inputMode="numeric"
            value={subject.askingPriceAtAnalysis}
            suffix="€"
            onChange={(value) => update("askingPriceAtAnalysis", Number(value) || undefined)}
            disabled={readOnly}
          />
        </MarketFieldSection>
      </div>
    </div>
  );
}
